import {
  useWSModalCustom,
  WSAlert,
  WSFlexBox,
  WSInfoBox,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { queriesInternalAccounts } from "@query";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { FromTo } from "../../../shared/components/FromTo";
import { selectorIsPlatformBalanceAccount } from "../../PlatformBalance/selectors/selectorIsPlatformBalanceAccount";
import { useInstantPayoutFee } from "../hooks/useInstantPayoutFee";
import { selectorMethodPayoutSpeed } from "../selectors/selectorMethodPayoutSpeed";
import { BadgePayoutPreference } from "./BadgePayoutPreference";
import { LabelMethod } from "./LabelMethod";
import { MethodValue } from "./ManageMethods/types";

type MethodWithSpeedProps = {
  method: MethodValue;
  // Undefined until the payout settings load, or when the account has no fee
  // configured — the badge then omits the percentage (PLA-3558)
  instantFeePercentage?: number;
};

// The destination alone can't describe the change: switching Standard ↔ Instant
// keeps the same destination, so the speed (and its fee) has to be shown too.
const MethodWithSpeed: React.FC<MethodWithSpeedProps> = props => {
  const payoutSpeed = selectorMethodPayoutSpeed(props.method);

  return (
    <WSFlexBox alignItems="center" gap="S">
      <LabelMethod type={props.method.type} id={props.method.id} />

      {payoutSpeed && (
        <BadgePayoutPreference
          payoutPreference={payoutSpeed}
          feePercentage={props.instantFeePercentage}
        />
      )}
    </WSFlexBox>
  );
};

type Props = {
  previousMethod: MethodValue;
  newMethod: MethodValue;
  onSave: () => void;
} & WSModalCustomProps<void>;

export const ModalConfirmPayoutMethodChange: React.FC<Props> = ({
  onSave,
  previousMethod,
  newMethod,
  ...modalProps
}) => {
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const instantFeePercentage = useInstantPayoutFee();
  const platformBalance = useMemo(
    () =>
      (queryInternalAccounts.data?.data || []).find(internalAccount =>
        selectorIsPlatformBalanceAccount(
          internalAccount,
          queryPayoutSettings.data
        )
      ),
    [queryInternalAccounts.data, queryPayoutSettings.data]
  );

  const isPlatformBalanceUnSelected = useMemo(() => {
    // A speed-only change keeps the Platform Balance as the destination, so the
    // "no longer stores your earnings" warning doesn't apply
    return (
      !!platformBalance &&
      previousMethod.id === platformBalance.id &&
      newMethod.id !== platformBalance.id
    );
  }, [platformBalance, previousMethod.id, newMethod.id]);

  return (
    <WSModal
      title="Change default payout method"
      size="M"
      actions={{
        alignment: "fill",
        buttons: [
          {
            kind: "Tertiary",
            label: "Cancel",
            onClick: () => {
              modalProps.onClose();
            }
          },
          {
            label: "Save changes",
            onClick: () => {
              onSave();
              modalProps.onClose();
            }
          }
        ]
      }}
      {...modalProps}
    >
      <WSList gap="L">
        <WSText.ParagraphSm color="gray500">
          Are you sure you want to change the default payout method?
        </WSText.ParagraphSm>

        <FromTo
          fromLabel="From"
          fromContent={
            <MethodWithSpeed
              method={previousMethod}
              instantFeePercentage={instantFeePercentage}
            />
          }
          toLabel="To"
          toContent={
            <MethodWithSpeed
              method={newMethod}
              instantFeePercentage={instantFeePercentage}
            />
          }
        />

        {isPlatformBalanceUnSelected && (
          <WSAlert
            title="Change default payout method"
            theme="info"
            icon="info-circle"
          >
            <WSInfoBox transparent>
              <p>
                When the Platform Balance is not the default payout method, it
                will no longer store your earnings and any remaining balance
                will be sent to your new default payout method (bank account).
              </p>
              <p>
                If you select Platform Balance as your default payout method in
                the future, you will be prompted to select a preferred currency
                again.
              </p>
            </WSInfoBox>
          </WSAlert>
        )}
      </WSList>
    </WSModal>
  );
};

export const useModalConfirmPayoutMethodChange = () =>
  useWSModalCustom(ModalConfirmPayoutMethodChange);
